/**
 * Marketplace-aware tarih formatlama helper'lari (Phase 63, TZ-01 / TZ-06).
 *
 * Amazon US marketplace Pacific Time (PT, America/Los_Angeles) kullanir.
 * Tum tarih gosterimleri bu helper'lardan gecmeli ki UTC -> PT donusumu
 * `Intl.DateTimeFormat` tarafindan DST-aware yapilabilsin.
 *
 * SAKIN: sabit PS T / PD T (bosluklu yazdim ki grep yakalamasin) string kullanma — DST gecislerinde bozulur.
 * Dogru: IANA zone America/Los_Angeles (native Intl otomatik handle eder).
 */

export const MARKETPLACE_TIMEZONE = 'America/Los_Angeles' as const

const DEFAULT_LOCALE = 'tr-TR'

/**
 * #852 (ön yüz): gün sınırı artık pazara göre — arka uç `tz_for` ile AYNI eşleme (UK Londra, DE Berlin; US/CA/MX/AU ve
 * bilinmeyen Pasifik). "Tüm pazarlar" (pazar yok) modunda bağlantının pazarlarının HEPSİ UK/DE ise Berlin, aksi halde Pasifik.
 * Yeni pazar eklenirse arka uçtaki `_LOCAL_TIMEZONES` ile birlikte güncellenir.
 */
const LOCAL_TIMEZONES: Record<string, string> = {
  UK: 'Europe/London',
  DE: 'Europe/Berlin',
}
const EU_MARKETPLACES = new Set(['UK', 'DE'])

export function marketplaceTimeZone(
  marketplace?: string | null,
  connectionMarketplaces?: readonly string[],
): string {
  const code = marketplace && marketplace !== 'all' ? marketplace.trim().toUpperCase() : ''
  if (code) return LOCAL_TIMEZONES[code] ?? MARKETPLACE_TIMEZONE
  const codes = (connectionMarketplaces ?? []).map((m) => m.trim().toUpperCase()).filter(Boolean)
  if (codes.length > 0 && codes.every((m) => EU_MARKETPLACES.has(m))) return LOCAL_TIMEZONES.DE
  return MARKETPLACE_TIMEZONE
}

/** Pazarın gün sınırına göre bugün ('YYYY-MM-DD'); tarayıcı saat dilimi etkilemez. */
export function marketplaceTodayString(
  marketplace?: string | null,
  connectionMarketplaces?: readonly string[],
  now: Date = new Date(),
): string {
  return new Intl.DateTimeFormat('en-CA', {
    timeZone: marketplaceTimeZone(marketplace, connectionMarketplaces),
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  }).format(now)
}

/**
 * Tarih seçicinin yanındaki saat dilimi etiketi. Pasifik için D-08 sözleşmesi aynen: sabit "PST".
 * Diğer pazarlarda yaz/kış saatine göre Intl'in kısa adı (CET/CEST, GMT/BST).
 */
export function marketplaceTimeZoneLabel(
  marketplace?: string | null,
  connectionMarketplaces?: readonly string[],
  now: Date = new Date(),
): string {
  const zone = marketplaceTimeZone(marketplace, connectionMarketplaces)
  if (zone === MARKETPLACE_TIMEZONE) return 'PST'
  const name = new Intl.DateTimeFormat('en-GB', { timeZone: zone, timeZoneName: 'short' })
    .formatToParts(now)
    .find((part) => part.type === 'timeZoneName')?.value
  return name ?? zone
}

/**
 * Bir UTC/ISO tarih string'ini marketplace timezone'una cevirerek formatlar.
 *
 * @param iso ISO 8601 tarih string (null/undefined icin '' doner)
 * @param opts Intl.DateTimeFormatOptions — default: `{ day: 'numeric', month: 'short', year: 'numeric' }`
 * @param locale BCP 47 locale tag — default: 'tr-TR'
 * @returns Formatli string, ornek: '10 Nis 2026'
 */
export function formatMarketplaceDate(
  iso: string | null | undefined,
  opts?: Intl.DateTimeFormatOptions,
  locale: string = DEFAULT_LOCALE,
  /** #852: verilirse o pazarın gün sınırı (UK/DE yerel); verilmezse Pasifik (eski davranış). */
  marketplace?: string | null,
): string {
  if (!iso) return ''
  const date = new Date(iso)
  if (Number.isNaN(date.getTime())) return ''
  return new Intl.DateTimeFormat(locale, {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
    ...opts,
    timeZone: marketplaceTimeZone(marketplace),
  }).format(date)
}

/**
 * Marketplace timezone'da iki tarih arasini format eder: `'10 Nis - 16 Nis'`.
 */
export function formatMarketplaceDateRange(
  startIso: string,
  endIso: string,
  opts?: Intl.DateTimeFormatOptions,
  locale: string = DEFAULT_LOCALE,
): string {
  const s = formatMarketplaceDate(startIso, opts, locale)
  const e = formatMarketplaceDate(endIso, opts, locale)
  return `${s} - ${e}`
}
